import { GeoAutocompletionType, GeoCoordinates, ManualGeoLoadResultArray } from '../../../@types/types'
import { AddressFeatureState } from '../../../@types/state-types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GeocodeObject } from '../../../@types/geographicInformationSystem-types'
import { EntityObject } from '../../../@types/geoAutocompletion-types'
import { hasUsableGeoCoordinates } from '../../../utils/geo-utils'

export const initialState: AddressFeatureState = {
  isPending: false,
  isLoading: false,
  locations: [],
  locationType: undefined,
  hasValidGeoCoordinates: true,
  geoCoordinates: {
    latitude: 0,
    longitude: 0,
    lambertEuCoordinatesX: 0,
    lambertEuCoordinatesY: 0,
  },
  manualGeoCoding: {
    countries: [],
    regions: [],
    cities: [],
    quarters: [],
  },
  isManualAddress: false,
  isInternationalAddress: false,
}
const addressFeatureSlice = createSlice({
  name: 'addressFeature',
  initialState,
  reducers: {
    setGisServiceRequestPending(state) {
      state.isPending = true
    },
    clearGisServiceRequestPending(state) {
      state.isPending = false
    },
    gisServiceRequest(state) {
      state.isLoading = true
    },
    gisServiceRequestCompleted(state) {
      state.isLoading = false
    },
    getGeoAutocompletionSuccess(
      state,
      action: PayloadAction<{ locations: EntityObject[]; type?: GeoAutocompletionType }>
    ) {
      const { locations, type } = action.payload
      state.locations = locations
      state.locationType = type && type.toLowerCase()
    },
    gisServiceSuccess(state, action: PayloadAction<{ coordinates: GeocodeObject }>) {
      const { geocodingResults } = action.payload.coordinates
      const coordinates = geocodingResults[0] as GeoCoordinates | undefined
      const hasValidGeoCoordinates = hasUsableGeoCoordinates(coordinates)
      state.geoCoordinates = hasValidGeoCoordinates ? coordinates : initialState.geoCoordinates
      state.hasValidGeoCoordinates = hasValidGeoCoordinates
      state.manualGeoCoding = hasValidGeoCoordinates
        ? { ...initialState.manualGeoCoding, regions: state.manualGeoCoding.regions }
        : state.manualGeoCoding
    },
    resetValidGeoCoordinates(state) {
      state.hasValidGeoCoordinates = true
      state.manualGeoCoding = initialState.manualGeoCoding
    },
    invalidateGeoCoordinates(state) {
      state.hasValidGeoCoordinates = false
      state.geoCoordinates = initialState.geoCoordinates
    },
    loadRegionsSuccess(state, action: PayloadAction<{ regions: ManualGeoLoadResultArray }>) {
      const { regions } = action.payload
      state.manualGeoCoding = { ...state.manualGeoCoding, regions }
      state.isManualAddress = true
      state.isInternationalAddress = false
    },
    loadCitiesSuccess(state, action: PayloadAction<{ cities: ManualGeoLoadResultArray }>) {
      const { cities } = action.payload
      state.manualGeoCoding = { ...state.manualGeoCoding, cities }
    },
    loadQuartersSuccess(state, action: PayloadAction<{ quarters: ManualGeoLoadResultArray }>) {
      const { quarters } = action.payload
      state.manualGeoCoding = { ...state.manualGeoCoding, quarters }
    },
    getInsertionGeoCoordinatesCompletionSuccess(state, action: PayloadAction<{ geoCoordinates: GeoCoordinates }>) {
      const { geoCoordinates } = action.payload
      state.hasValidGeoCoordinates = hasUsableGeoCoordinates(geoCoordinates)
      state.geoCoordinates = state.hasValidGeoCoordinates ? { ...geoCoordinates } : initialState.geoCoordinates
    },
    loadCountriesSuccess(state, action: PayloadAction<{ countries: ManualGeoLoadResultArray }>) {
      const { countries } = action.payload
      state.manualGeoCoding = { ...state.manualGeoCoding, countries: countries }
      state.isManualAddress = false
      state.isInternationalAddress = true
    },
    loadInternationalRegionsSuccess(state, action: PayloadAction<{ regions: ManualGeoLoadResultArray }>) {
      const { regions } = action.payload
      state.isManualAddress = false
      state.isInternationalAddress = true
      state.manualGeoCoding = { ...state.manualGeoCoding, regions }
    },
    resetAddressFeature(state, action: PayloadAction<{ isNational: boolean }>) {
      const { isNational } = action.payload
      state.isInternationalAddress = !isNational
      state.geoCoordinates = initialState.geoCoordinates
      state.manualGeoCoding = { ...initialState.manualGeoCoding, countries: state.manualGeoCoding.countries }
    },
    getInsertionInternationalAddressCompletionSuccess(state) {
      state.isInternationalAddress = true
    },
    getInsertionManualGeoCodingCompletionSuccess(state) {
      state.hasValidGeoCoordinates = false
      state.isManualAddress = true
    },
  },
})

export const {
  getInsertionManualGeoCodingCompletionSuccess,
  getInsertionInternationalAddressCompletionSuccess,
  resetAddressFeature,
  loadInternationalRegionsSuccess,
  loadCountriesSuccess,
  getInsertionGeoCoordinatesCompletionSuccess,
  loadQuartersSuccess,
  loadCitiesSuccess,
  loadRegionsSuccess,
  resetValidGeoCoordinates,
  invalidateGeoCoordinates,
  gisServiceSuccess,
  getGeoAutocompletionSuccess,
  gisServiceRequestCompleted,
  gisServiceRequest,
  clearGisServiceRequestPending,
  setGisServiceRequestPending,
} = addressFeatureSlice.actions
export default addressFeatureSlice.reducer
